import React, { useMemo, useState } from 'react'
import { ColumnChart } from 'react-chartkick'
// Note: react-chartkick requires an adapter (e.g., `chartkick/chart.js`).
// Ensure the `chartkick` package is installed and imported at app entry if missing.
import { BarTitle, BarChartWrap, BarLegend, BarLegendItem, BarLegendLabel, PieColorSwatch } from './SupervisionStyle'

export type StatusBarData = {
  // Values for single-status bars (not Completed)
  upcoming: number
  due: number
  inProgress: number
  overdue: number
  submitted: number
  // Completed split
  completedPositive: number
  completedNegative: number
  submittedPositive: number
  submittedNegative: number
}

type Props = {
  title?: string
  data: StatusBarData
}

const SupervisionBar: React.FC<Props> = ({ title = '', data }) => {
  const [visible, setVisible] = useState<{ positive: boolean; negative: boolean; count: boolean }>({
    positive: true,
    negative: true,
    count: true,
  })

  // Block hiding the last visible item
  const toggle = (key: keyof typeof visible) => {
    setVisible(prev => {
      // If we are about to hide it (current is true), ensure >1 remain
      const isActive = prev[key]
      const count = Object.values(prev).filter(Boolean).length
      if (isActive && count <= 1) return prev
      return { ...prev, [key]: !isActive }
    })
  }

  const fullSeries: any[] = [
    {
      name: 'Positive mood',
      color: 'rgba(142, 255, 176, 1)', // green
      data: { Completed: data.completedPositive },
    },
    {
      name: 'Negative mood',
      color: 'rgba(255, 185, 182, 1)', // red
      data: { Completed: data.completedNegative },
    },
    {
      name: 'Others',
      color: 'rgba(125, 168, 255, 1)', // blue
      data: {
        Upcoming: data.upcoming,
        'To be completed': data.due,
        'In progress': data.inProgress,
        Overdue: data.overdue,
      },
    },
    {
      name: 'Positive mood',
      color: 'rgba(142, 255, 176, 1)', // green
      data: { Submitted: data.submittedPositive },
    },
    {
      name: 'Negative mood',
      color: 'rgba(255, 185, 182, 1)', // red
      data: { Submitted: data.submittedNegative },
    },
  ]

  const filteredSeries = useMemo(() => {
    return fullSeries.filter(s => {
      if (s.name === 'Positive mood') return visible.positive
      if (s.name === 'Negative mood') return visible.negative
      if (s.name === 'Others') return visible.count
      return true
    })
  }, [fullSeries, visible])

  return (
    <div>
      {title ? <BarTitle>{title}</BarTitle> : null}

      <BarChartWrap>
        <ColumnChart
          data={filteredSeries}
          stacked={true}
          legend={false}
          height="501px"
          ytitle="No. of supervisions"
          library={{
            maintainAspectRatio: false,
            plugins: {
              // ✅ TURN OFF PERCENT + VALUE LABELS ON BARS
              datalabels: { display: false },
              labels: { display: false, render: () => '' }, // ⛔ disables chartjs-plugin-labels
            },
            scales: {
              xAxes: [
                {
                  stacked: true,
                  gridLines: { display: false },
                  ticks: { fontColor: 'rgba(0, 0, 0, 0.7)', fontSize: 12 },
                  barThickness: 75,
                },
              ],
              yAxes: [
                {
                  stacked: true,
                  ticks: { beginAtZero: true, fontColor: 'rgba(0, 0, 0, 0.7)', fontSize: 12 },
                  gridLines: { display: false },
                  scaleLabel: {
                    display: true, // must be true to show styles
                    labelString: 'No. of supervisions', // Chartkick sets this via ytitle, but safe to repeat
                    fontColor: 'rgba(136, 136, 136, 1)',
                    fontSize: 14,
                    fontStyle: '500', // or 'bold'
                    padding: 5,
                  },
                },
              ],
            },
            tooltips: {
              mode: 'index',
              intersect: false,
              backgroundColor: '#ffffff',
              bodyFontColor: 'rgba(0, 0, 0, 1)',
              bodySpacing: 20,
              borderColor: 'rgba(0,0,0,0.06)',
              borderWidth: 1,
              cornerRadius: 23,
              caretSize: 0,
              xPadding: 20,
              yPadding: 20,
              displayColors: true,
              // For Completed: show Positive & Negative. For others: show only Count.
              filter: (tooltipItem: any, dataObj: any) => {
                const labels = (dataObj && dataObj.labels) || []
                const xLabel = labels[tooltipItem.index] || tooltipItem.xLabel
                const ds = dataObj.datasets[tooltipItem.datasetIndex]
                if (!ds) return false
                if (xLabel === 'Completed' || xLabel === 'Submitted') {
                  return (ds.label || ds.name) !== 'Others' && ds.data && ds.data[tooltipItem.index] != null
                }
                return (ds.label || ds.name) === 'Others' && ds.data && ds.data[tooltipItem.index] != null
              },
              callbacks: {
                title: () => '',
                labelColor: (tooltipItem: any, chart: any) => {
                  const ds = chart.config.data.datasets[tooltipItem.datasetIndex]
                  const bg = (ds && (ds.backgroundColor || ds.color)) || '#ccc'
                  return { backgroundColor: bg, borderColor: bg }
                },
                label: (item: any, dataObj: any) => {
                  const labels = (dataObj && dataObj.labels) || []
                  const xLabel = labels[item.index] || item.xLabel
                  const ds = dataObj.datasets[item.datasetIndex]
                  const seriesLabel = ds && (ds.label || ds.name) ? ds.label || ds.name : ''
                  const val =
                    typeof item.yLabel !== 'undefined' ? item.yLabel : ds && ds.data ? ds.data[item.index] : ''
                  // For Count series, show the category label instead of 'Count'
                  const displayLabel = seriesLabel === 'Others' ? xLabel : seriesLabel
                  return `  ${displayLabel}  -  ${val}`
                },
              },
            },
          }}
        />
        <BarLegend>
          <BarLegendItem onClick={() => toggle('positive')} style={{ cursor: 'pointer', opacity: visible.positive ? 1 : 0.5 }}>
            <PieColorSwatch bg="rgba(142, 255, 176, 1)" />
            <BarLegendLabel>Positive mood</BarLegendLabel>
          </BarLegendItem>
          <BarLegendItem onClick={() => toggle('negative')} style={{ cursor: 'pointer', opacity: visible.negative ? 1 : 0.5 }}>
            <PieColorSwatch bg="rgba(255, 185, 182, 1)" />
            <BarLegendLabel>Negative mood</BarLegendLabel>
          </BarLegendItem>
          <BarLegendItem onClick={() => toggle('count')} style={{ cursor: 'pointer', opacity: visible.count ? 1 : 0.5 }}>
            <PieColorSwatch bg="rgba(125, 168, 255, 1)" />
            <BarLegendLabel>Pending</BarLegendLabel>
          </BarLegendItem>
        </BarLegend>
      </BarChartWrap>
    </div>
  )
}

export default SupervisionBar
